import API from "../../lib/TimeEditAPI";
import { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { TSupportContact } from "../../types/api/shared";
import Language from "../../lib/Language";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import { Dropdown, MenuProps } from "@timeedit/ui-components/antd";
import { Typography } from "@timeedit/ui-components/components/Typography/Typography";
import TooltippedTopbarButton from "./TooltippedTopbarButton";
import { TOPBAR_BUTTON_ICON_HEIGHT } from "../../lib/UIConstants";
import { IN_APP_ASSISTANT_COPY } from "@timeedit/shared-ai-sdk/in-app-assistant/react";
import TopbarSupportAgent from "./TopbarSupportAgent";
import { Ai } from "@timeedit/ui-components/components/Icon/Icons/index";
import { purple6 } from "@timeedit/ui-components/theme/generated/tokens";

type THelpMenuProps = {
    onHelpCenterClicked: MenuProps["onClick"];
    onShortcutsClicked: MenuProps["onClick"];
    onAboutClicked: (supportInfoLines: string[], showSupportText: boolean) => void;
    onResetHelpClicked: MenuProps["onClick"];
};

const HelpMenu = (props: THelpMenuProps) => {
    const [supportInfoLines, setSupportInfoLines] = useState<string[]>([]);
    const [supportContacts, setSupportContacts] = useState<TSupportContact[]>([]);
    const [supportAgentOpen, setSupportAgentOpen] = useState(false);
    const flags = useFlags();
    const showSupportAgent = Boolean(flags.coreShowSupportAgent);

    useEffect(() => {
        API.getSupportInfo((_supportURL, supportInfoLines, supportContacts) => {
            setSupportInfoLines(supportInfoLines || []);
            setSupportContacts(supportContacts || []);
        });
    }, []);

    const renderItem = (text: string, link: string | undefined) => {
        if (!text) {
            return null;
        }
        if (link) {
            return (
                <div>
                    <Typography.Link
                        openInNewTab={true}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            window.open(link, "_blank");
                        }}
                        href={link}
                    >
                        {text}
                    </Typography.Link>
                </div>
            );
        }
        return <Typography.Text>{text}</Typography.Text>;
    };

    const renderContact = (contact: TSupportContact) => {
        return (
            <div>
                {renderItem(contact.name, undefined)}
                {renderItem(contact.email, `mailto:${contact.email}`)}
                {renderItem(contact.phone, `tel:${contact.phone}`)}
                {renderItem(contact.url_title || contact.url, contact.url)}
                {renderItem(contact.manual_url_title || contact.manual_url, contact.manual_url)}
            </div>
        );
    };

    const renderSupportContacts = () => {
        let result: NonNullable<MenuProps["items"]> = [];
        if (supportContacts.length > 0) {
            result.push({ type: "divider" });
        }
        supportContacts.forEach((contact, index) => {
            result.push({
                key: `label:${index}`,
                className: "contactCard",
                label: renderContact(contact),
                title: contact.description,
            });
        });
        return result.filter((elm) => elm !== null);
    };

    const supportAgentItems: NonNullable<MenuProps["items"]> = showSupportAgent
        ? [
              {
                  key: "supportAgent",
                  label: (
                      <span className="tw:inline-flex tw:items-center tw:gap-2">
                          {IN_APP_ASSISTANT_COPY.title}
                          <Ai aria-hidden={true} style={{ color: purple6 }} />
                      </span>
                  ),
                  onClick: () => setSupportAgentOpen(true),
              },
          ]
        : [];

    const helpDropdown = (
        <Dropdown
            trigger={["click"]}
            className={`tw:h-full
        tw:flex tw:items-center tw:justify-center tw:overflow-hidden
        tw:text-inherit tw:leading-[0]
      `}
            menu={{
                items: [
                    ...supportAgentItems,
                    {
                        key: "helpCenter",
                        label: Language.get("nc_help_center"),
                        onClick: props.onHelpCenterClicked,
                    },
                    {
                        key: "shortcuts",
                        label: Language.get("nc_keyboard_shortcuts"),
                        onClick: props.onShortcutsClicked,
                    },
                    {
                        key: "about",
                        label: Language.get("about_window_title"),
                        onClick: () => {
                            props.onAboutClicked(supportInfoLines, false);
                        },
                    },
                    {
                        key: "resetHelp",
                        label: Language.get("nc_menu_reset_help"),
                        onClick: props.onResetHelpClicked,
                    },
                    ...renderSupportContacts(),
                ],
            }}
        >
            <TooltippedTopbarButton
                tooltip={Language.get("menu_help")}
                icon={
                    <Icon
                        id="QuestionCircleOutlined"
                        size={TOPBAR_BUTTON_ICON_HEIGHT}
                        alt={Language.get("menu_help")}
                    />
                }
                onClick={() => {}}
            />
        </Dropdown>
    );

    if (!showSupportAgent) {
        return helpDropdown;
    }

    return (
        <TopbarSupportAgent open={supportAgentOpen} onClose={() => setSupportAgentOpen(false)}>
            {helpDropdown}
        </TopbarSupportAgent>
    );
};

export default HelpMenu;
